import { LOCALE_KEYS } from "@foodlista/shared/i18n/constants"
import { strictI18nSchema } from "@foodlista/shared/i18n/schemas"
import { z } from "zod"

const itemWithIdsSchema = z.object({
	id: z.string().describe("Unique and immutable id of the item"),
	title: z.string().describe("The title of the item"),
	description: z
		.string()
		.nullable()
		.describe(
			"The description of the item, usually the list of the ingredients",
		),
	price: z.number().describe("The price of the item, without currency symbol"),
})

const subsectionWithIdsSchema = z.object({
	id: z.string().describe("Unique and immutable id of the subsection"),
	title: z.string().describe("The title of the subsection"),
	items: z.array(itemWithIdsSchema).describe("The items of the subsection"),
})

export const menuSchemaWithIds = z.object({
	locale: z.enum(LOCALE_KEYS).describe("The detected language of the menu"),
	sections: z
		.array(
			z.object({
				id: z.string().describe("Unique and immutable id of the section"),
				title: z.string().describe("The title of the section"),
				items: z.array(itemWithIdsSchema).describe("The items of the section"),
				subsections: z
					.array(subsectionWithIdsSchema)
					.optional()
					.describe("The subsections of the section, if present"),
			}),
		)
		.describe("The sections of the menu"),
})

const translatedItemSchema = z.object({
	id: z.string().describe("Unique and immutable id of the item"),
	title: strictI18nSchema.describe(
		"The translated title of the item for each locale",
	),
	description: strictI18nSchema
		.nullable()
		.describe("The translated description of the item for each locale"),
	price: z.number().describe("The price of the item, without currency symbol"),
})

const translatedSubsectionSchema = z.object({
	id: z.string().describe("Unique and immutable id of the subsection"),
	title: strictI18nSchema.describe(
		"The translated title of the subsection for each locale",
	),
	items: z
		.array(translatedItemSchema)
		.describe("The translated items of the subsection"),
})

export const translatedMenuSchema = z.object({
	sections: z
		.array(
			z.object({
				id: z.string().describe("Unique and immutable id of the section"),
				title: strictI18nSchema.describe(
					"The translated title of the section for each locale",
				),
				items: z
					.array(translatedItemSchema)
					.describe("The translated items of the section"),
				subsections: z
					.array(translatedSubsectionSchema)
					.optional()
					.describe("The translated subsections of the section, if present"),
			}),
		)
		.describe("The translated sections of the menu"),
})

export const menuTranslateInputSchema = z.object({
	flowId: z.string().uuid(),
	/**
	 * Target menu to enrich. Present for the menu-creation flow (section and
	 * subsection titles are persisted); absent for the product-catalog flow,
	 * where only the products are enriched.
	 */
	menuId: z.string().uuid().optional(),
	/**
	 * Unread by the run. It rides in the payload so the BOC can link the
	 * finished operation to the menu page, which is addressed by slug.
	 */
	menuSlug: z.string().min(1).max(200).optional(),
	menu: menuSchemaWithIds,
})

/**
 * The run persists the translations server-side (keyed by the ids in
 * `menu`), so the output only reports whether the model succeeded. `false`
 * means the entity keeps its source-locale content.
 */
export const menuTranslateOutputSchema = z.object({
	success: z.boolean(),
})

/** Semantic phases persisted by the Trigger run for the BOC. */
export type MenuTranslateProgressPhase =
	| { kind: "translating" }
	| { kind: "verifying" }
	| { kind: "fallback" }
	| { kind: "done" }

/** Translated menu content (i18n titles/descriptions keyed by immutable id). */
export type MenuTranslatedContent = z.infer<typeof translatedMenuSchema>
export type MenuTranslateInput = z.infer<typeof menuTranslateInputSchema>
export type MenuTranslateOutput = z.infer<typeof menuTranslateOutputSchema>
